import { daysFromCivil } from '../shared/dates';
import { CarState, Input, carPerf } from './car';
import { freshLap } from './laps';
import { Mods, applyMods, combineMods } from './mods';
import { mulberry32 } from './rng';
import { TEAMS, TeamSpec, teamById } from './teams';
import { Track } from './track';
import { CALENDAR, trackDefById } from './tracks/index';
import { TICK_HZ } from './tuning';
import { World } from './world';

/**
 * The Daily Grand Prix (design.md B14.3): one combination a day for
 * everyone (track, car, conditions, twist), unlimited practice and one
 * ranked run. A ranked run is a standing-start lap from the grid, like the
 * original's qualifying: crash or miss a checkpoint and it's a DNF.
 *
 * Everything here is deterministic, so the server re-runs a submitted
 * run from its inputs and accepts it only if it reproduces exactly
 * (design.md B14.10 anti-cheat).
 */

/** Daily #1. */
export const DAILY_EPOCH = '2026-10-13';
/** A ranked run longer than this is a DNF (and caps the upload size). */
export const DAILY_MAX_TICKS = 150 * TICK_HZ;

export type Conditions = 'dry' | 'wet' | 'night';
export type TwistId = 'none' | 'turbo' | 'grass' | 'heavy' | 'tyres';

export const CONDITIONS: Record<Conditions, { label: string; desc: string; mods: Mods }> = {
  dry: { label: 'DRY', desc: 'Dry track.', mods: {} },
  wet: { label: 'WET', desc: 'Wet track: less grip, a little less top speed.', mods: { grip: 0.7, vmax: 0.97 } },
  night: { label: 'NIGHT', desc: 'Night race: you only see what your lights show.', mods: {} },
};

export const TWISTS: Record<TwistId, { label: string; desc: string; mods: Mods }> = {
  none: { label: 'NO TWIST', desc: 'Just you and the clock.', mods: {} },
  turbo: { label: 'TURBO', desc: '+8% top speed for everyone.', mods: { vmax: 1.08 } },
  grass: { label: 'GRASS IS FASTER', desc: 'Going off-road costs far less than usual.', mods: { offroad: 0.4 } },
  heavy: { label: 'HEAVY FUEL', desc: 'Slower off the line and out of corners.', mods: { accel: 0.8 } },
  tyres: { label: 'WORN TYRES', desc: 'Less grip in every corner.', mods: { grip: 0.75 } },
};

export interface DailySpec {
  date: string;
  number: number;
  trackId: string;
  teamId: string;
  conditions: Conditions;
  twist: TwistId;
}

export function dailyNumber(date: string): number {
  return daysFromCivil(date) - daysFromCivil(DAILY_EPOCH) + 1;
}

function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** The six circuits in a shuffled order per 6-day block; no track two days running. */
function blockOrder(block: number): number[] {
  const rnd = mulberry32(hashString(`block:${block}`));
  const ids = CALENDAR.map((_, i) => i);
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [ids[i], ids[j]] = [ids[j], ids[i]];
  }
  return ids;
}

function trackForDay(dayIdx: number): number {
  const n = CALENDAR.length;
  const block = Math.floor(dayIdx / n);
  const order = blockOrder(block);
  if (block > 0) {
    const prev = blockOrder(block - 1);
    if (order[0] === prev[n - 1]) [order[0], order[1]] = [order[1], order[0]];
  }
  return order[dayIdx - block * n];
}

export function dailySpec(date: string): DailySpec {
  const number = dailyNumber(date);
  const dayIdx = Math.max(0, number - 1);
  const rnd = mulberry32(hashString(`daily:${date}`));
  const trackId = CALENDAR[trackForDay(dayIdx)].id;
  const teamId = TEAMS[Math.floor(rnd() * TEAMS.length)].id;
  const c = rnd();
  const conditions: Conditions = c < 0.55 ? 'dry' : c < 0.8 ? 'wet' : 'night';
  const t = rnd();
  let twist: TwistId = t < 0.3 ? 'none' : t < 0.48 ? 'turbo' : t < 0.66 ? 'grass' : t < 0.83 ? 'heavy' : 'tyres';
  if (conditions === 'wet' && twist === 'tyres') twist = 'none'; // too slippery to be fun
  return { date, number, trackId, teamId, conditions, twist };
}

export function dailyMods(spec: DailySpec): Mods {
  return combineMods(CONDITIONS[spec.conditions].mods, TWISTS[spec.twist].mods);
}

export function dailyTeam(spec: DailySpec): TeamSpec {
  return teamById(spec.teamId);
}

const trackCache = new Map<string, Track>();
export function trackById(id: string): Track {
  let t = trackCache.get(id);
  if (!t) {
    t = new Track(trackDefById(id));
    trackCache.set(id, t);
  }
  return t;
}

export function dailyWorld(spec: DailySpec): World {
  return new World(trackById(spec.trackId), dailyTeam(spec), 0, dailyMods(spec));
}

export type RunStatus = 'ok' | 'dnf';

export interface RunResult {
  status: RunStatus;
  ticks: number | null;
  /** Ticks from the start line to each checkpoint. */
  splits: number[];
  /** Index of the input on which the run ended (-1 if it never did). */
  endTick: number;
  reason: 'lap' | 'crash' | 'missed' | 'invalid' | 'timeout' | 'incomplete';
}

/**
 * Drive a world from its current state with `inputs` until the run ends:
 * a completed lap (ok), a crash, a missed checkpoint, or the time limit.
 */
export function runToEnd(world: World, inputs: readonly Input[], maxTicks = DAILY_MAX_TICKS): RunResult {
  const splits: number[] = [];
  const n = Math.min(inputs.length, maxTicks);
  for (let i = 0; i < n; i++) {
    world.step(inputs[i]);
    for (const e of world.events) {
      if (e.type === 'crash') return { status: 'dnf', ticks: null, splits, endTick: i, reason: 'crash' };
      if (e.type === 'missedCheckpoint') return { status: 'dnf', ticks: null, splits, endTick: i, reason: 'missed' };
      if (e.type === 'checkpoint') splits.push(world.tick - world.lap.startTick);
      if (e.type === 'lap') {
        return e.valid
          ? { status: 'ok', ticks: e.ticks, splits, endTick: i, reason: 'lap' }
          : { status: 'dnf', ticks: null, splits, endTick: i, reason: 'invalid' };
      }
    }
  }
  if (inputs.length >= maxTicks) return { status: 'dnf', ticks: null, splits, endTick: maxTicks - 1, reason: 'timeout' };
  return { status: 'dnf', ticks: null, splits, endTick: -1, reason: 'incomplete' };
}

export function runDaily(spec: DailySpec, inputs: readonly Input[]): RunResult {
  return runToEnd(dailyWorld(spec), inputs);
}

/** A Time Trial lap as saved with its ghost (design.md B14.10): verify by replay. */
export interface LapClaim {
  track: string;
  team: string;
  ticks: number;
  start: { x: number; y: number; vx: number; vy: number; h: number; bonusMph: number; streakDist: number };
  inputs: Input[];
}

/** Replays a flying lap; returns its time in ticks if it completes validly, else null. */
export function verifyLap(claim: LapClaim): number | null {
  const team = teamById(claim.team);
  if (team.id !== claim.team || !CALENDAR.some((d) => d.id === claim.track)) return null;
  let track: Track;
  try {
    track = trackById(claim.track);
  } catch {
    return null;
  }
  const w = new World(track, team);
  const s = claim.start;
  const car: CarState = { ...w.car, x: s.x, y: s.y, vx: s.vx, vy: s.vy, h: s.h, bonusMph: Math.max(0, Math.min(5, Math.floor(s.bonusMph))) };
  w.car = car;
  w.perf = applyMods(carPerf(team, car.bonusMph), null);
  // The lap began on the start line on the previous tick.
  if (track.surfaceAt(s.x, s.y) === 'wall' || !inStartZone(track, s.x, s.y)) return null;
  w.lap = { ...freshLap(), startTick: 0, nextGate: track.gates.length > 1 ? 1 : 0, lastGate: 0, streakDist: Math.max(0, s.streakDist) };
  const r = runToEnd(w, claim.inputs, 120 * TICK_HZ);
  if (r.status !== 'ok' || r.endTick !== claim.inputs.length - 1) return null;
  return r.ticks;
}

/** The ghost start must sit just past the start line, not anywhere on the lap. */
function inStartZone(track: Track, x: number, y: number): boolean {
  const g = track.gates[0];
  const dx = x - g.cx;
  const dy = y - g.cy;
  return dx * dx + dy * dy < 80 * 80;
}

// ------------------------------------------------------------ input codec

/**
 * Run-length encoded inputs, base64url: [count varint][steer][throttle][brake]
 * per run, each value stored as byte (v + 127). A 30 s keyboard lap is ~1 KB.
 */
export function encodeInputs(inputs: readonly Input[]): string {
  const bytes: number[] = [];
  let i = 0;
  while (i < inputs.length) {
    const a = inputs[i];
    let j = i + 1;
    while (j < inputs.length && inputs[j].steer === a.steer && inputs[j].throttle === a.throttle && inputs[j].brake === a.brake) j++;
    let n = j - i;
    while (n >= 0x80) {
      bytes.push((n & 0x7f) | 0x80);
      n >>>= 7;
    }
    bytes.push(n);
    bytes.push(a.steer + 127, a.throttle + 127, a.brake + 127);
    i = j;
  }
  return toBase64Url(bytes);
}

export function decodeInputs(s: string, maxTicks = DAILY_MAX_TICKS): Input[] | null {
  const bytes = fromBase64Url(s);
  if (!bytes) return null;
  const out: Input[] = [];
  let p = 0;
  while (p < bytes.length) {
    let n = 0;
    let shift = 0;
    for (;;) {
      if (p >= bytes.length || shift > 21) return null;
      const b = bytes[p++];
      n |= (b & 0x7f) << shift;
      if (!(b & 0x80)) break;
      shift += 7;
    }
    if (p + 3 > bytes.length || n <= 0) return null;
    const steer = bytes[p++] - 127;
    const throttle = bytes[p++] - 127;
    const brake = bytes[p++] - 127;
    if (steer < -127 || steer > 127 || throttle < 0 || throttle > 127 || brake < 0 || brake > 127) return null;
    if (out.length + n > maxTicks) return null;
    const inp = { steer, throttle, brake };
    for (let k = 0; k < n; k++) out.push(inp);
  }
  return out;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function toBase64Url(bytes: number[]): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2] : 0;
    const v = (a << 16) | (b << 8) | c;
    s += B64[(v >> 18) & 63] + B64[(v >> 12) & 63];
    if (i + 1 < bytes.length) s += B64[(v >> 6) & 63];
    if (i + 2 < bytes.length) s += B64[v & 63];
  }
  return s;
}

function fromBase64Url(s: string): number[] | null {
  if (!/^[A-Za-z0-9_-]*$/.test(s) || s.length % 4 === 1) return null;
  const out: number[] = [];
  for (let i = 0; i < s.length; i += 4) {
    const chunk = s.slice(i, i + 4);
    const v = [...chunk].map((ch) => B64.indexOf(ch));
    const n = (v[0] << 18) | (v[1] << 12) | ((v[2] ?? 0) << 6) | (v[3] ?? 0);
    out.push((n >> 16) & 255);
    if (chunk.length > 2) out.push((n >> 8) & 255);
    if (chunk.length > 3) out.push(n & 255);
  }
  return out;
}

